/**
 * Настройки вида, языка и доступности (спецификация § 14.1–14.5) — перевод Настроек
 * человека в готовое оформление экрана.
 *
 * Здесь нет ни браузера, ни вёрстки (ADR-0002): это чистый пересчёт «что человек выбрал»
 * в «как это выглядит». Оболочка берёт готовые числа и раскладывает их по своим правилам —
 * сайт в переменные стилей, настольное приложение и телефон в свои. Разойдись этот
 * пересчёт по оболочкам, один и тот же человек увидел бы на двух устройствах разное.
 *
 * Про подсказки устройства. Тема «как в системе» и просьба системы убрать движение
 * приходят снаружи, отдельным доводом: сама система — это уже платформа, а ядро о ней
 * знать не должно. Просьба системы убрать движение **сильнее** выключенной настройки:
 * человек, отметивший это в системе, отметил это для всех приложений сразу, и
 * переспрашивать его в каждом — значит не услышать с первого раза.
 */

/** Языки мессенджера (§ 11). Держатся теми же строками, что и на сервере. */
export type Language = 'ru' | 'en' | 'he';

/** Направление письма следует за языком и вручную не задаётся (§ 14.4). */
export type Direction = 'ltr' | 'rtl';

/** Что оболочка получает на руки и просто применяет. */
export interface Appearance {
  /** «Как в системе» сюда не доходит: к этому месту выбор уже сделан. */
  theme: 'light' | 'dark' | 'high_contrast';
  /** Множитель к основному размеру текста. Не пиксели: у каждого экрана они свои. */
  textScale: number;
  fontFamily: 'default' | 'serif' | 'mono' | 'readable';
  /** Насыщенность в общепринятых числах: 400 — обычный, 700 — жирный. */
  fontWeight: number;
  /** Межстрочный интервал — во сколько раз строка выше своего текста. */
  lineHeight: number;
  /** Множитель к отступам: плотный экран сжимает пустоту, но не текст. */
  spacingScale: number;
  language: Language;
  direction: Direction;
  /** 24 часа или 12 с «до полудня» — считает оболочка, но решает эта Настройка. */
  timeFormat: '24h' | '12h';
  /** С какого дня начинается неделя: 0 — воскресенье, 6 — суббота. */
  weekStart: number;
  /** `reduced` — переходы показываются мгновенно, без движения. */
  motion: 'full' | 'reduced';
  underlineLinks: boolean;
  /**
   * Наименьшая сторона кнопки в независимых точках. Обычная — 24: меньше в неё не
   * попасть дрожащей рукой. Крупные цели (§ 14.5) поднимают её до 44.
   */
  minimumTarget: number;
}

/** Что подсказало устройство. Ничего не подсказало — обходимся выбором человека. */
export interface DeviceHints {
  /** Система оформлена тёмной. Нужно только теме «как в системе». */
  prefersDark?: boolean;
  /** Система просит меньше движения. Перебивает выключенную настройку. */
  prefersReducedMotion?: boolean;
}

const TEXT_SCALE: Readonly<Record<string, number>> = {
  small: 0.875,
  compact: 0.9375,
  normal: 1,
  large: 1.125,
  huge: 1.375,
};

const LINE_HEIGHT: Readonly<Record<string, number>> = {
  tight: 1.3,
  normal: 1.5,
  loose: 1.8,
};

/** Языки, где пишут справа налево. Иврит здесь один — арабского в сборке нет. */
const RIGHT_TO_LEFT: readonly Language[] = ['he'];

const LANGUAGES: readonly Language[] = ['ru', 'en', 'he'];

/** Обычная и крупная наименьшая сторона кнопки, в независимых точках. */
export const TARGET_NORMAL = 24;
export const TARGET_LARGE = 44;

const text = (settings: Record<string, unknown>, key: string, fallback: string): string => {
  const value = settings[key];
  return typeof value === 'string' ? value : fallback;
};

const yes = (settings: Record<string, unknown>, key: string, fallback: boolean): boolean => {
  const value = settings[key];
  return typeof value === 'boolean' ? value : fallback;
};

export const directionOf = (language: Language): Direction =>
  RIGHT_TO_LEFT.includes(language) ? 'rtl' : 'ltr';

/**
 * Тема. «Как в системе» разрешается подсказкой устройства; подсказки нет — светлая:
 * тёмный экран, показанный тому, кто его не просил, читается как поломка.
 */
const themeOf = (chosen: string, hints: DeviceHints): Appearance['theme'] => {
  if (chosen === 'light' || chosen === 'dark' || chosen === 'high_contrast') return chosen;
  return hints.prefersDark ? 'dark' : 'light';
};

/**
 * Перевести Настройки в оформление. Настройки приходят такими, какими их отдал сервер:
 * он уже подставил умолчания и заготовку фирмы, и второй раз этого делать незачем.
 * Но выдумывать за сервер тоже нельзя — незнакомое значение здесь молча становится
 * обычным, а не роняет экран: человек без единой настройки хуже, чем человек без одной.
 */
export const appearanceOf = (
  settings: Record<string, unknown>,
  hints: DeviceHints = {},
): Appearance => {
  const chosenLanguage = text(settings, 'language', 'ru');
  const language = (LANGUAGES as readonly string[]).includes(chosenLanguage)
    ? (chosenLanguage as Language)
    : 'ru';
  const timeFormat = text(settings, 'timeFormat', '24h') === '12h' ? '12h' : '24h';
  const weekStartRaw = settings['weekStart'];
  // Число вне недели — это не «восьмой день», а мусор из версии новее нашей. Свернуть его
  // остатком от деления значило бы молча переставить человеку календарь на день, которого
  // он не выбирал; воскресенье хотя бы честно видно как «настройка не применилась».
  const weekStart =
    typeof weekStartRaw === 'number' &&
    Number.isSafeInteger(weekStartRaw) &&
    weekStartRaw >= 0 &&
    weekStartRaw <= 6
      ? weekStartRaw
      : 0;
  const family = text(settings, 'fontFamily', 'default');

  return {
    theme: themeOf(text(settings, 'theme', 'system'), hints),
    textScale: TEXT_SCALE[text(settings, 'textSize', 'normal')] ?? TEXT_SCALE['normal']!,
    fontFamily:
      family === 'serif' || family === 'mono' || family === 'readable' ? family : 'default',
    fontWeight: text(settings, 'fontWeight', 'normal') === 'bold' ? 700 : 400,
    lineHeight: LINE_HEIGHT[text(settings, 'lineHeight', 'normal')] ?? LINE_HEIGHT['normal']!,
    // Плотный экран убирает четверть пустоты — но только пустоты: сам текст остаётся
    // того размера, который человек выбрал отдельной Настройкой.
    spacingScale: text(settings, 'density', 'comfortable') === 'compact' ? 0.75 : 1,
    language,
    direction: directionOf(language),
    timeFormat,
    weekStart,
    motion: yes(settings, 'reduceMotion', false) || hints.prefersReducedMotion ? 'reduced' : 'full',
    underlineLinks: yes(settings, 'underlineLinks', true),
    minimumTarget: yes(settings, 'largeTargets', false) ? TARGET_LARGE : TARGET_NORMAL,
  };
};

/**
 * Сетка Раскладки при письме справа налево **не зеркалится** (G12): справа налево идёт
 * текст внутри Блока, а Блоки стоят там, куда их поставил Администратор. Иначе фирма,
 * собравшая экран, увидела бы его перевёрнутым — и решила бы, что кто-то всё переставил.
 */
export const layoutDirectionOf = (): Direction => 'ltr';
